import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Send, CheckCircle, Download, Video, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Function to upload video
const uploadVideoBase64 = async (videoBase64) => {
  const response = await fetch(`${API_URL}/api/videos/upload-base64`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ video_data: videoBase64 }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Upload failed');
  }
  
  return response.json();
};

export default function Step6Confirmation({ allData }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [sendEmailRecap, setSendEmailRecap] = useState(false);

  const handleSubmit = async () => {
    if (!legalAccepted) {
      setShowLegalModal(true);
      return;
    }

    setSubmitting(true);
    
    const now = new Date();
    const emailTimestamp = now.toLocaleString('fr-FR', { 
      dateStyle: 'full', 
      timeStyle: 'long',
      timeZone: 'Europe/Paris'
    });

    try {
      // Check if we need to retry video upload
      let videoData = allData.videoData || { skip_video: true };
      
      if (videoData.upload_pending && videoData.video_blob_base64) {
        setUploadingVideo(true);
        toast.info('Envoi de la vidéo en cours...');
        
        try {
          const uploadResult = await uploadVideoBase64(videoData.video_blob_base64);
          videoData = {
            ...videoData,
            video_id: uploadResult.video_id,
            video_url: uploadResult.url,
            upload_pending: false,
            video_blob_base64: undefined // Don't send blob to server
          };
          toast.success('Vidéo uploadée avec succès !');
        } catch (uploadError) {
          console.error('Video retry upload failed:', uploadError);
          toast.warning('La vidéo n\'a pas pu être envoyée. Le formulaire sera quand même enregistré.');
        }
        setUploadingVideo(false);
      }

      // Remove blob from data before sending
      const cleanVideoData = { ...videoData };
      delete cleanVideoData.video_blob_base64;

      // Préparer les données pour l'envoi
      const submissionData = {
        client_info: allData.clientInfo || {},
        form_validation: allData.formValidation || {},
        video_data: cleanVideoData,
        signature_data: allData.signatureData || {},
        avis_data: allData.avisData || {},
        legal_accepted: true,
        legal_accepted_at: now.toISOString(),
        submission_timestamp: emailTimestamp,
        send_email_recap: sendEmailRecap,
      };

      // Envoyer au backend
      const submitHeaders = {
        'Content-Type': 'application/json',
      };
      const userToken = localStorage.getItem('user_token');
      if (userToken) {
        submitHeaders['Authorization'] = `Bearer ${userToken}`;
      }

      const response = await fetch(`${API_URL}/api/submissions/simple`, {
        method: 'POST',
        headers: submitHeaders,
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de l\'envoi');
      }

      const result = await response.json();
      setSubmissionId(result.id);
      setSubmissionSuccess(true);
      toast.success('Formulaire enregistré avec succès !');
      
      setTimeout(() => {
        navigate('/success');
      }, 2000);

    } catch (error) {
      console.error('Submission error:', error);
      // Sauvegarder localement en cas d'erreur réseau
      try {
        localStorage.setItem('pending_submission', JSON.stringify({
          data: allData,
          timestamp: now.toISOString()
        }));
      } catch (e) {}
      
      toast.error('Connexion instable. Vos données ont été sauvegardées localement.');
      setSubmitting(false);
    }
  };

  const handleLegalAccept = () => {
    setLegalAccepted(true);
    setShowLegalModal(false);
    toast.success('Déclaration légale acceptée');
    // Auto submit after accepting
    setTimeout(() => {
      handleSubmit();
    }, 500);
  };

  // Télécharger l'export Excel
  const handleDownloadExcel = async () => {
    try {
      toast.info('Génération du fichier Excel...');
      const response = await fetch(`${API_URL}/api/export/excel`);
      
      if (!response.ok) {
        throw new Error('Aucune donnée à exporter');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `soumissions_litige_protect_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Fichier Excel téléchargé !');
    } catch (error) {
      console.error('Excel download error:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  if (submissionSuccess) {
    return (
      <div className="space-y-6 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-success/10 mb-4">
          <CheckCircle className="w-10 h-10 text-success" strokeWidth={1.5} />
        </div>
        <h2 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Formulaire soumis !
        </h2>
        <p className="text-lg text-foreground/80">
          Redirection en cours...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Legal Modal */}
      {showLegalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" onClick={() => setShowLegalModal(false)}>
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[95vh] overflow-y-auto p-4 sm:p-6 my-2" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Déclaration légale finale
            </h2>
            
            <div className="space-y-4 text-xs sm:text-sm text-foreground/80">
              <p className="font-medium text-foreground">
                Avant d'envoyer votre formulaire, veuillez lire et accepter la déclaration suivante :
              </p>
              
              <div className="bg-accent/5 border border-accent/20 rounded-lg p-3 sm:p-4 space-y-3">
                <p><strong>Je, soussigné(e) {allData.clientInfo?.civilite} {allData.clientInfo?.nom} {allData.clientInfo?.prenom}, déclare sur l'honneur :</strong></p>
                
                <ul className="space-y-2 list-disc list-inside">
                  <li>Avoir lu, compris et accepté l'intégralité des informations présentées dans ce formulaire</li>
                  <li>Avoir coché toutes les cases de validation en toute connaissance de cause</li>
                  <li>Avoir apposé ma signature électronique de manière libre et éclairée</li>
                  <li>Avoir validé mon identité par code SMS</li>
                  <li>Avoir autorisé la géolocalisation pour authentifier ma signature</li>
                  <li>Reconnaître que cette démarche constitue un engagement contractuel</li>
                  <li>Accepter que toutes mes réponses, ma signature, l'horodatage et ma position GPS soient conservés comme preuves juridiques</li>
                  <li>Avoir été informé(e) de mes droits, notamment le délai de rétractation de 14 jours</li>
                  <li>Confirmer que cette validation a été effectuée sans pression ni contrainte</li>
                </ul>

                <p className="mt-4 font-medium">
                  Je comprends que ce document horodaté et géolocalisé a valeur de preuve juridique conformément au règlement eIDAS européen.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-foreground/60">
                  <strong>Protection des données (RGPD) :</strong> Vos données personnelles sont traitées et conservées de manière sécurisée. 
                  Vous disposez d'un droit d'accès, de rectification et de suppression. Pour exercer vos droits, contactez l'entreprise.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                onClick={() => setShowLegalModal(false)}
                className="flex-1 h-12 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm sm:text-base"
              >
                Relire le formulaire
              </button>
              <button
                data-testid="accept-legal-btn"
                onClick={handleLegalAccept}
                className="flex-1 h-12 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 font-medium text-sm sm:text-base"
              >
                J'accepte et je valide
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-2">
          <CheckCircle className="w-8 h-8 text-accent" strokeWidth={1.5} />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Confirmation
        </h2>
        <p className="text-sm sm:text-base text-foreground/80">
          Vérifiez vos informations avant l'envoi
        </p>
      </div>

      <div className="space-y-4">
        <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-3">
          <h3 className="font-semibold text-base sm:text-lg border-b border-border pb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Informations client
          </h3>
          <div className="space-y-2 text-xs sm:text-sm">
            <p><span className="text-foreground/60">Nom :</span> <span className="font-medium">{allData.clientInfo?.civilite} {allData.clientInfo?.nom} {allData.clientInfo?.prenom}</span></p>
            <p><span className="text-foreground/60">Téléphone :</span> <span className="font-medium">{allData.clientInfo?.portable1}</span></p>
            <p><span className="text-foreground/60">Email :</span> <span className="font-medium">{allData.clientInfo?.email}</span></p>
            <p><span className="text-foreground/60">Adresse :</span> <span className="font-medium">{allData.clientInfo?.adresse}, {allData.clientInfo?.code_postal} {allData.clientInfo?.ville}</span></p>
            <p><span className="text-foreground/60">Société :</span> <span className="font-medium">{allData.clientInfo?.societe_installatrice}</span></p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-3">
          <h3 className="font-semibold text-base sm:text-lg border-b border-border pb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Formulaire de validation
          </h3>
          <div className="space-y-2 text-xs sm:text-sm">
            <p><span className="text-foreground/60">Type :</span> <span className="font-medium">{allData.formValidation?.form_type === 'commercial' ? 'Questions Commercial' : 'Après Installation'}</span></p>
            <p><span className="text-foreground/60">Validations :</span> <span className="font-medium text-success">✓ {Object.keys(allData.formValidation?.checkboxes || {}).filter(k => allData.formValidation?.checkboxes[k]).length} cases cochées</span></p>
            {allData.formValidation?.timestamp && (
              <p><span className="text-foreground/60">Horodatage :</span> <span className="font-medium mono text-xs">{allData.formValidation.timestamp}</span></p>
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-3">
          <h3 className="font-semibold text-base sm:text-lg border-b border-border pb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Vidéo témoignage
          </h3>
          <div className="space-y-2 text-xs sm:text-sm">
            {allData.videoData?.skip_video ? (
              <p className="text-foreground/60">Aucune vidéo (passé)</p>
            ) : (
              <>
                <p><span className="text-foreground/60">Vidéo :</span> <span className="font-medium text-success">✓ Enregistrée</span></p>
                <p><span className="text-foreground/60">Autorisation diffusion :</span> <span className="font-medium text-success">✓ Acceptée</span></p>
              </>
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-3">
          <h3 className="font-semibold text-base sm:text-lg border-b border-border pb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Signature électronique
          </h3>
          <div className="space-y-3">
            {allData.signatureData?.signature_data && (
              <div className="border border-border rounded-lg p-2 bg-white">
                <img 
                  src={allData.signatureData?.signature_data} 
                  alt="Signature"
                  className="w-full h-24 sm:h-32 object-contain"
                />
              </div>
            )}
            <p className="text-xs sm:text-sm"><span className="text-foreground/60">SMS :</span> <span className="font-medium text-success">✓ Vérifié</span></p>
            {allData.signatureData?.timestamp && (
              <p className="text-xs sm:text-sm"><span className="text-foreground/60">Horodatage :</span> <span className="font-medium font-mono text-xs block mt-1">{allData.signatureData.timestamp}</span></p>
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-3">
          <h3 className="font-semibold text-base sm:text-lg border-b border-border pb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Avis client
          </h3>
          <div className="space-y-2 text-xs sm:text-sm">
            {allData.avisData?.skip_avis ? (
              <p className="text-foreground/60">Aucun avis (passé)</p>
            ) : (
              <>
                <p><span className="text-foreground/60">Note :</span> <span className="font-medium">{allData.avisData?.rating}/5 ⭐</span></p>
                <p><span className="text-foreground/60">Avis :</span> <span className="font-medium text-success">✓ Enregistré</span></p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Email recap checkbox */}
        <label
          data-testid="send-email-checkbox-label"
          className="flex items-start gap-3 bg-card border border-border rounded-lg p-4 cursor-pointer hover:border-accent/40 transition-colors"
        >
          <input
            data-testid="send-email-checkbox"
            type="checkbox"
            checked={sendEmailRecap}
            onChange={(e) => setSendEmailRecap(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-input text-accent focus:ring-accent"
          />
          <div>
            <p className="font-medium text-sm sm:text-base">Envoyer le récapitulatif par email au client</p>
            <p className="text-xs sm:text-sm text-foreground/60 mt-1">
              Un email de confirmation sera envoyé à <span className="font-medium break-all">{allData.clientInfo?.email || 'N/A'}</span>
            </p>
          </div>
        </label>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs sm:text-sm text-foreground/80">
          <p className="font-medium mb-2">Validation finale</p>
          <p>
            En cliquant sur "Envoyer", vous confirmez l'exactitude des informations fournies.
            Ce document constitue une preuve juridique de votre démarche.
          </p>
        </div>

        <button
          data-testid="submit-form-btn"
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full h-12 sm:h-14 bg-accent text-accent-foreground text-base sm:text-lg font-medium rounded-md hover:bg-accent/90 border-b-2 border-accent-foreground/20 active:border-b-0 active:translate-y-[2px] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Send className="w-5 h-5" strokeWidth={1.5} />
          {submitting ? 'Envoi en cours...' : 'Envoyer le formulaire'}
        </button>
      </div>
    </div>
  );
}
